'use client';
import React from 'react';
import Link from 'next/link';
import { VersionPill } from './VersionPill';
import { DesignSpecButton } from './DesignSpecButton';
import { Icon } from './Icon';
import { Container } from './Container';
import { FE_ONLY, PORTAL_VERSION, CHANNEL_LABEL } from '@/lib/flags';
import { useAuth, logout } from '@/hooks/useAuth';

export function Header({ onToggleSidebar }: { onToggleSidebar(): void }) {
  const { isAuthenticated, loading } = useAuth();
  
  return (
    <header className="fixed inset-x-0 top-0 z-50 h-14 bg-white border-b border-[#e5e1d6]">
      <div className="h-full flex">
        {/* Sidebar area with menu button */}
        <div className="flex items-center gap-3 px-4" style={{ width: 'var(--sidebar-w)' }}>
          <button onClick={onToggleSidebar} className="inline-flex items-center justify-center rounded-md border px-2.5 py-1.5 hover:bg-gray-50" aria-label="Toggle sidebar">
            <Icon name="menu" className="w-4 h-4" />
          </button>
        </div>

        {/* Main content area - constrained to same width as page content */}
        <div className="flex-1 flex items-center pl-6">
          <div className="w-full max-w-[1400px] flex items-center justify-between pr-4">
            <div className="flex items-center gap-3">
              <Link href="/" className="font-semibold tracking-tight text-gray-900 flex items-center gap-3">
                <span className="bg-[#111827] text-white rounded-md w-7 h-7 inline-flex items-center justify-center text-[10px] font-bold">TT</span>
                <span className="text-lg">TinyTinyTelg Portal</span>
              </Link>
              <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-gray-700 bg-white/70">{CHANNEL_LABEL}</span>
              <span className="text-gray-400">·</span>
              <span className="inline-flex items-center gap-1 text-sm text-[#3f6b3a]" title="Online">
                <span className="inline-block w-2 h-2 rounded-full bg-[#3f6b3a]" />
                Online
              </span>
              {FE_ONLY && (
                <span className="ml-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-gray-700 bg-amber-50 border-amber-300">
                  Frontend-only
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <VersionPill version={PORTAL_VERSION} />
              <DesignSpecButton />
              {isAuthenticated && !loading && (
                <button 
                  onClick={logout} 
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-gray-50"
                >
                  <Icon name="logout" className="w-4 h-4" />
                  Logout
                </button>
              )}
              {!isAuthenticated && !loading && (
                <Link 
                  href="/login"
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm hover:bg-gray-50"
                >
                  <Icon name="login" className="w-4 h-4" />
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
